/**
 * Parsing the GM guide.
 *
 * The guide ships as plain text, which keeps it readable outside the app and
 * editable without markup. That leaves one genuine ambiguity: a section
 * heading ("2. Your Job") and a numbered list item ("2. Keep the world
 * locally plausible.") are the same shape.
 *
 * They differ in what follows them. A heading is separated from its body by a
 * blank line; a list item is followed immediately by the next item.
 *
 * That alone is not quite enough: the *last* item of a list is also followed
 * by a blank line. So a numbered line is only a heading when no list is
 * currently open — inside a list, it is the next item.
 */

export interface GuideBlock {
  kind: 'paragraph' | 'list';
  lines: string[];
}

export interface GuideSection {
  id: string;
  number: number | null;
  title: string;
  blocks: GuideBlock[];
}

export interface Guide {
  title: string;
  subtitle: string;
  sections: GuideSection[];
}

const HEADING = /^(\d+)\.\s+(.+)$/;
const LIST_ITEM = /^(?:\d+\.|[-•*])\s+/;

export function parseGuide(raw: string): Guide {
  const lines = raw.replace(/\r\n/g, '\n').split('\n');

  const title = lines[0]?.trim() ?? 'Guide';
  const subtitle = lines[1]?.trim() ?? '';

  const sections: GuideSection[] = [];
  let current: GuideSection = {
    id: 'intro',
    number: null,
    title: 'Introduction',
    blocks: [],
  };
  let buffer: string[] = [];
  let bufferIsList = false;

  const flush = () => {
    if (buffer.length === 0) return;
    current.blocks.push({
      kind: bufferIsList ? 'list' : 'paragraph',
      lines: [...buffer],
    });
    buffer = [];
    bufferIsList = false;
  };

  for (let i = 2; i < lines.length; i += 1) {
    const line = (lines[i] ?? '').trim();
    const next = (lines[i + 1] ?? '').trim();
    const match = HEADING.exec(line);

    // A heading is a numbered line whose body starts on a later line — but
    // only when no list is open, since a list's final item also has a blank
    // line after it.
    if (match && next === '' && !bufferIsList) {
      flush();
      if (current.blocks.length > 0 || current.id !== 'intro') sections.push(current);
      current = {
        id: `section-${match[1]}`,
        number: Number(match[1]),
        title: match[2] ?? '',
        blocks: [],
      };
      continue;
    }

    if (line === '') {
      flush();
      continue;
    }

    const isItem = LIST_ITEM.test(line);
    if (buffer.length > 0 && isItem !== bufferIsList) flush();
    bufferIsList = isItem;
    buffer.push(isItem ? line.replace(LIST_ITEM, '') : line);
  }

  flush();
  sections.push(current);

  return {
    title,
    subtitle,
    sections: sections.filter((s) => s.blocks.length > 0),
  };
}
